import Link from "next/link";
import Image from "next/image";
import { memo, useEffect, useMemo, useState } from "react";
import { calculateSurcharge } from "@/utils/pricingUtils";
import { ListingTypeCornerBadge } from "@/components/ListingTypeBadge";
import { serviceDetailHref } from "@/lib/servicesConfig";

const formatPrice = (price) => {
  const numericPrice = Number(price);
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(numericPrice) ? numericPrice : 0);
};

const ServiceCard = memo(({ data, lcp = false, prefetchService = true, prefetchShop = true }) => {
  const [pricingEpoch, setPricingEpoch] = useState(0);
  const [userPrefsHydrated, setUserPrefsHydrated] = useState(false);

  useEffect(() => {
    const bump = () => setPricingEpoch((n) => n + 1);
    window.addEventListener("userLoggedIn", bump);
    return () => window.removeEventListener("userLoggedIn", bump);
  }, []);
  useEffect(() => setUserPrefsHydrated(true), []);

  const { userInfo, userCurrency, processed, serviceHref } = useMemo(() => {
    if (!data || typeof data !== "object") {
      return { userInfo: {}, userCurrency: false, processed: null, serviceHref: "#" };
    }
    const userInfoData =
      typeof window !== "undefined" && userPrefsHydrated
        ? JSON.parse(localStorage.getItem("userInfo") || "{}")
        : {};
    const sellerCountry =
      typeof data?.seller?.country === "string"
        ? data.seller.country.trim()
        : data?.seller?.country?.name
          ? String(data.seller.country.name).trim()
          : "";
    const currency =
      sellerCountry && userInfoData?.currency && userInfoData?.currencyRate
        ? userInfoData.currency
        : false;
    const processedData = calculateSurcharge([data], {
      ignoreStoredUserInfo: !userPrefsHydrated,
    })?.[0];
    const verticalSlug = processedData?.Category?.slug || "beauty-and-personal-care";
    const subSlug = processedData?.SubCategory?.slug;
    const href = subSlug
      ? serviceDetailHref(verticalSlug, subSlug, processedData?.slug || data.slug)
      : "#";

    return {
      userInfo: userInfoData,
      userCurrency: currency,
      processed: processedData,
      serviceHref: href,
    };
  }, [data, pricingEpoch, userPrefsHydrated]);

  if (!data || !processed) return null;

  const imageUrl = processed.images?.[0]?.imageUrl || "/placeholder.jpg";
  const cardImageSizes =
    "(max-width: 639px) min(92vw, 320px), min(300px, min(100vw, 320px))";
  const serviceTitle = processed.productName?.trim();
  const imageLinkLabel = serviceTitle ? `View ${serviceTitle}` : "View service";
  const priceType = processed.serviceDetails?.priceType || "fixed";
  const pricePrefix = priceType === "from" ? "From " : "";
  const displayPrice = parseFloat(
    processed.surTotalAmount ?? processed.finalPrice ?? processed.price ?? 0
  );
  const duration = processed.serviceDetails?.durationMinutes;

  return (
    <div className="relative mx-auto flex w-full min-w-0 max-w-[320px] shrink-0 flex-col text-left">
      <Link
        href={serviceHref}
        aria-label={imageLinkLabel}
        className="group relative block w-full shrink-0 cursor-pointer"
        prefetch={prefetchService}
        scroll
      >
        <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-slate-200 bg-orange-50/80">
          <div className="absolute inset-0 z-0 overflow-hidden [&>span]:block [&>span]:!h-full [&>span]:!w-full [&>span]:max-h-full [&_img]:!h-full [&_img]:!w-full [&_img]:max-h-none [&_img]:object-cover [&_img]:object-center">
            <Image
              src={imageUrl}
              alt=""
              fill
              className="object-cover object-center"
              sizes={cardImageSizes}
              priority={lcp}
              fetchPriority={lcp ? "high" : "auto"}
              loading={lcp ? undefined : "lazy"}
              decoding="async"
              quality={lcp ? 66 : 56}
              unoptimized={imageUrl.startsWith("data:")}
            />
          </div>
          <ListingTypeCornerBadge product={processed} className="absolute right-3 top-3 z-20" />
          <div
            className="absolute inset-0 z-[1] bg-yellow-950/[55%] opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none"
            aria-hidden
          />
          <div className="absolute bottom-9 left-0 right-0 z-20 flex items-center justify-center">
            <span className="pointer-events-none opacity-0 transition-opacity buttonprimary group-hover:opacity-100">
              View service
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="7.477"
                height="13.14"
                viewBox="0 0 7.477 13.14"
                className="inline"
              >
                <path
                  d="M26.166,46.727a.559.559,0,0,1-.4-.164l-5.606-5.606a.561.561,0,0,1,.793-.793l5.21,5.21,5.21-5.21a.561.561,0,0,1,.793.793l-5.606,5.606a.559.559,0,0,1-.4.164Z"
                  transform="translate(-39.625 32.764) rotate(-90)"
                  fill="#fff"
                  stroke="#fff"
                  strokeWidth="0.75"
                />
              </svg>
            </span>
          </div>
        </div>
      </Link>
      <div className="flex flex-col">
        <p className="mt-3 mb-1 min-w-0 text-left text-sm font-normal leading-snug text-blue-950 md:text-base lg:text-lg">
          <Link
            href={serviceHref}
            prefetch={prefetchService}
            scroll
            className="block truncate hover:text-primary"
          >
            {processed.productName}
          </Link>
        </p>
        <div className="mb-1 text-left text-sm font-bold leading-snug text-blue-950 md:text-base lg:text-lg">
          {pricePrefix}
          {userCurrency ? String(userCurrency).replace(/\$/g, "") : "CAD"}{" "}
          {formatPrice(displayPrice)}
        </div>
        <p className="mb-1 text-left text-sm text-slate-600 underline">
          <Link
            href={`/shop/${processed?.seller?.storeSlug}`}
            prefetch={prefetchShop}
            className="inline-block"
          >
            {processed?.seller?.storeTitle ||
              [processed?.seller?.firstName, processed?.seller?.lastName]
                .filter(Boolean)
                .join(" ")}
          </Link>
        </p>
        {duration ? (
          <div className="mb-0 flex flex-wrap gap-1">
            <div className="w-max rounded-full bg-[#a0e193] px-2 py-0.5 text-[11px] text-blue-950">
              {duration} min
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});

ServiceCard.displayName = "ServiceCard";

export default ServiceCard;
